export type AnimationParams = {
  draw: (args: AnimationDrawParams) => void
  onEnd?: () => void
  timer?: number
}
export type AnimationDrawParams = { initTime: number; timeDiff: number; progress?: number }
export type AnimationDrawReturn = ReturnType<typeof Animation.getInstance>

export default class Animation {
  static getInstance(params: AnimationParams) {
    const { play, stop, kill, setInitTime } = new Animation(params)

    return {
      play,
      stop,
      kill,
      setInitTime,
    }
  }

  constructor(params: AnimationParams) {
    this.params = params

    this.loop = this.loop.bind(this)
    this.play = this.play.bind(this)
    this.stop = this.stop.bind(this)
    this.kill = this.kill.bind(this)
    this.setInitTime = this.setInitTime.bind(this)
  }

  private params: AnimationParams

  private state: {
    rafId: ReturnType<typeof window.requestAnimationFrame>
    refreshId: ReturnType<typeof setInterval>
    initTimestamp: number
  } = {
    rafId: null,
    refreshId: null,
    initTimestamp: 0,
  }

  private loop() {
    const timeDiff = Date.now() - this.state.initTimestamp
    const progress = this.params.timer && timeDiff / this.params.timer

    this.params.draw({ initTime: this.state.initTimestamp, timeDiff, progress: progress > 1 ? 1 : progress })

    this.state.rafId = window.requestAnimationFrame(this.loop)
  }

  private play() {
    this.state.initTimestamp = Date.now()
    this.loop()

    if (this.params.timer) {
      setTimeout(this.stop, this.params.timer)
    } else {
      // refresh the infinite animation once per 5min
      // to optimize calculations with growing [timeDiff]
      this.state.refreshId = setInterval(this.setInitTime, 3e5)
    }

    return this.stop
  }

  private kill() {
    const isStopped = !!this.state.rafId

    window.cancelAnimationFrame(this.state.rafId)
    this.state.rafId = null
    clearInterval(this.state.refreshId)
    this.state.refreshId = null

    return isStopped
  }

  private stop() {
    const isStopped = this.kill()

    // do not run [onEnd] if the process wasn't started
    if (isStopped) {
      this.params.onEnd?.()
    }
  }

  private setInitTime(time?: number) {
    this.state.initTimestamp = time || Date.now()
  }
}
